import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftAddon?: ReactNode;
  rightAddon?: ReactNode;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      hint,
      leftAddon,
      rightAddon,
      leftIcon,
      rightIcon,
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');

    const baseInputStyles = clsx(
      'w-full px-4 py-2.5 text-slate-700 placeholder-slate-400 transition-all duration-200',
      'focus:outline-none focus:ring-2',
      error
        ? 'border-danger-300 focus:border-danger-400 focus:ring-danger-100'
        : 'border-slate-200 focus:border-primary-400 focus:ring-primary-100',
      leftAddon ? 'rounded-l-none' : 'rounded-l-xl',
      rightAddon ? 'rounded-r-none' : 'rounded-r-xl',
      leftIcon && 'pl-10',
      rightIcon && 'pr-10'
    );

    const hasAddons = leftAddon || rightAddon;

    return (
      <div className={className}>
        {label && (
          <label
            htmlFor={inputId}
            className={clsx(
              'block text-sm font-medium mb-1.5',
              error ? 'text-danger-500' : 'text-slate-700'
            )}
          >
            {error || label}
          </label>
        )}

        <div className={clsx('relative', hasAddons && 'flex')}>
          {leftAddon && (
            <div className="flex items-center justify-center px-4 py-2.5 bg-slate-100 border border-r-0 border-slate-200 rounded-l-xl text-slate-500 text-sm font-medium">
              {leftAddon}
            </div>
          )}

          <div className="relative flex-1">
            {leftIcon && (
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                {leftIcon}
              </div>
            )}

            <input
              ref={ref}
              id={inputId}
              className={clsx(baseInputStyles, 'border bg-white')}
              {...props}
            />

            {rightIcon && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                {rightIcon}
              </div>
            )}
          </div>

          {rightAddon && (
            <div className="flex items-center justify-center px-4 py-2.5 bg-slate-100 border border-l-0 border-slate-200 rounded-r-xl text-slate-500 text-sm font-medium">
              {rightAddon}
            </div>
          )}
        </div>

        {hint && !error && (
          <p className="mt-1.5 text-sm text-slate-500">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;


